import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { WhatsAppProvider } from './contexts/WhatsAppContext';
import { PersonalWaSessionProvider } from './contexts/PersonalWaSessionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ActiveBulkSendProvider } from './contexts/ActiveBulkSendContext';
import ProtectedRoute from './components/ProtectedRoute';
import PageLoader from './components/PageLoader';
import Layout from './components/Layout';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Chat = lazy(() => import('./pages/Chat'));
const Contacts = lazy(() => import('./pages/Contacts'));
const QuickReplies = lazy(() => import('./pages/QuickReplies'));
const WhatsAppTemplates = lazy(() => import('./pages/WhatsAppTemplates'));
const PersonalTemplates = lazy(() => import('./pages/PersonalTemplates'));
const TemplateSendHistory = lazy(() => import('./pages/TemplateSendHistory'));
const PersonalTemplateSendHistory = lazy(() => import('./pages/PersonalTemplateSendHistory'));
const MediaGallery = lazy(() => import('./pages/MediaGallery'));
const Notifications = lazy(() => import('./pages/Notifications'));

// Super Admin pages
const Companies = lazy(() => import('./pages/super-admin/Companies'));
const Users = lazy(() => import('./pages/super-admin/Users'));
const Packages = lazy(() => import('./pages/super-admin/Packages'));
const SuperAdminWhatsAppConfigs = lazy(() => import('./pages/super-admin/WhatsAppConfigs'));
const BookingBotTester = lazy(() => import('./pages/super-admin/BookingBotTester'));
const Analytics = lazy(() => import('./pages/super-admin/Analytics'));
const SuperAdminSalesInquiries = lazy(() => import('./pages/sales/SalesInquiries'));

// Admin pages
const Agents = lazy(() => import('./pages/admin/Agents'));
const AdminWhatsAppConfigs = lazy(() => import('./pages/admin/WhatsAppConfigs'));
const AdminSalesInquiries = lazy(() => import('./pages/sales/SalesInquiries'));

// Agent pages
const AgentPersonalWhatsApp = lazy(() => import('./pages/agent/PersonalWhatsApp'));

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main:    '#22c55e',
      dark:    '#16a34a',
      light:   '#4ade80',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3b82f6',
      dark: '#2563eb',
      light: '#60a5fa',
    },
    error:   { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    info:    { main: '#3b82f6' },
    success: { main: '#22c55e' },
    background: {
      default: '#f8fafc',
      paper:   '#ffffff',
    },
    text: {
      primary:   '#0f172a',
      secondary: '#64748b',
      disabled:  '#94a3b8',
    },
    divider: '#e2e8f0',
    grey: {
      50:  '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', color: '#64748b' },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '-0.005em' },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 4px 6px -1px rgba(0,0,0,0.07)',
    '0 10px 15px -3px rgba(0,0,0,0.08)',
    '0 20px 25px -5px rgba(0,0,0,0.08)',
    '0 0 0 1px rgba(0,0,0,0.04),0 2px 8px rgba(0,0,0,0.06)',
    '0 0 0 1px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.08)',
    '0 0 0 1px rgba(0,0,0,0.04),0 8px 24px rgba(0,0,0,0.1)',
    '0 0 0 1px rgba(0,0,0,0.04),0 12px 32px rgba(0,0,0,0.12)',
    '0 0 0 1px rgba(0,0,0,0.04),0 16px 40px rgba(0,0,0,0.12)',
    '0 0 0 1px rgba(0,0,0,0.04),0 20px 48px rgba(0,0,0,0.14)',
    '0 0 0 1px rgba(0,0,0,0.04),0 24px 56px rgba(0,0,0,0.14)',
    '0 0 0 1px rgba(0,0,0,0.04),0 28px 64px rgba(0,0,0,0.14)',
    '0 0 0 1px rgba(0,0,0,0.04),0 32px 72px rgba(0,0,0,0.16)',
    '0 0 0 1px rgba(0,0,0,0.04),0 36px 80px rgba(0,0,0,0.16)',
    '0 0 0 1px rgba(0,0,0,0.04),0 40px 88px rgba(0,0,0,0.16)',
    '0 0 0 1px rgba(0,0,0,0.04),0 44px 96px rgba(0,0,0,0.18)',
    '0 0 0 1px rgba(0,0,0,0.04),0 48px 104px rgba(0,0,0,0.18)',
    '0 0 0 1px rgba(0,0,0,0.04),0 52px 112px rgba(0,0,0,0.18)',
    '0 0 0 1px rgba(0,0,0,0.04),0 56px 120px rgba(0,0,0,0.20)',
    '0 0 0 1px rgba(0,0,0,0.04),0 60px 128px rgba(0,0,0,0.20)',
    '0 0 0 1px rgba(0,0,0,0.04),0 64px 136px rgba(0,0,0,0.20)',
    '0 0 0 1px rgba(0,0,0,0.04),0 68px 144px rgba(0,0,0,0.22)',
    '0 0 0 1px rgba(0,0,0,0.04),0 72px 152px rgba(0,0,0,0.22)',
  ] as any,
  components: {
    // Button
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 18px',
          fontWeight: 600,
          fontSize: '0.875rem',
          transition: 'all 200ms cubic-bezier(0.4,0,0.2,1)',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 6px 16px rgba(34,197,94,0.3)' },
        },
        outlined: {
          borderColor: '#e2e8f0',
          '&:hover': { borderColor: '#22c55e', background: 'rgba(34,197,94,0.04)' },
        },
      },
    },
    // TextField
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: '#ffffff',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#94a3b8',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#22c55e',
            borderWidth: 2,
            boxShadow: '0 0 0 3px rgba(34,197,94,0.12)',
          },
        },
        notchedOutline: {
          borderColor: '#e2e8f0',
          transition: 'all 200ms cubic-bezier(0.4,0,0.2,1)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          color: '#64748b',
          '&.Mui-focused': { color: '#22c55e' },
        },
      },
    },
    // Card
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #e2e8f0',
          borderRadius: 14,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          transition: 'box-shadow 200ms ease, transform 200ms ease',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '1.25rem', '&:last-child': { paddingBottom: '1.25rem' } },
      },
    },
    // Paper
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid #e2e8f0',
          borderRadius: 14,
        },
      },
    },
    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 26,
        },
      },
    },
    // Table
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            background: '#f8fafc',
            color: '#64748b',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid #e2e8f0',
            padding: '12px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background 150ms ease',
            '&:hover': { background: '#f8fafc' },
          },
          '& .MuiTableCell-body': {
            borderBottom: '1px solid #f1f5f9',
            padding: '14px 16px',
            fontSize: '0.9375rem',
          },
        },
      },
    },
    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: '1px solid',
        },
        standardError: { borderColor: '#fecaca', background: '#fef2f2' },
        standardSuccess: { borderColor: '#bbf7d0', background: '#f0fdf4' },
        standardWarning: { borderColor: '#fde68a', background: '#fffbeb' },
        standardInfo: { borderColor: '#bfdbfe', background: '#eff6ff' },
      },
    },
    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#0f172a',
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px',
        },
        arrow: { color: '#0f172a' },
      },
    },
    // Dialog
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.0625rem',
          fontWeight: 700,
          color: '#0f172a',
          padding: '1.25rem 1.5rem 0.75rem',
        },
      },
    },
    // Menu
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: '1px solid #e2e8f0',
          boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
          animation: 'scaleIn 0.15s ease-out',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          padding: '8px 10px',
          fontSize: '0.875rem',
          fontWeight: 500,
          '&:hover': { background: '#f8fafc' },
          '&.Mui-selected': { background: 'rgba(34,197,94,0.08)', '&:hover': { background: 'rgba(34,197,94,0.12)' } },
        },
      },
    },
    // Badge
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.65rem',
          fontWeight: 700,
          minWidth: 18,
          height: 18,
          padding: '0 4px',
          borderRadius: 9,
        },
      },
    },
    // Select
    MuiSelect: {
      styleOverrides: {
        select: { borderRadius: 10 },
      },
    },
    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '0.875rem',
          borderRadius: 10,
        },
      },
    },
    // Fab
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.16)' },
        },
      },
    },
    // LinearProgress
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 9999, height: 6 },
        bar: { borderRadius: 9999 },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <WhatsAppProvider>
          <SocketProvider>
            <PersonalWaSessionProvider>
            <NotificationProvider>
              <Router>
                <Routes>
                  <Route
                    path="/login"
                    element={
                      <Suspense fallback={<PageLoader fullScreen message="Loading..." />}>
                        <Login />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <ActiveBulkSendProvider>
                          <Layout>
                            <Suspense fallback={<PageLoader message="Loading page..." />}>
                              <Routes>
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              <Route path="/dashboard" element={<Dashboard />} />
                              <Route path="/chat" element={<Chat />} />
                              <Route path="/chat/:whatsAppNumberId" element={<Chat />} />
                              <Route path="/chat/:whatsAppNumberId/:contactId" element={<Chat />} />
                              <Route path="/contacts" element={<Contacts />} />
                              <Route path="/quick-replies" element={<QuickReplies />} />
                              <Route path="/whatsapp-templates" element={<WhatsAppTemplates />} />
                              <Route path="/personal-templates" element={<PersonalTemplates />} />
                              <Route path="/template-send-history" element={<TemplateSendHistory />} />
                              <Route path="/personal-template-send-history" element={<PersonalTemplateSendHistory />} />
                              <Route path="/media-gallery" element={<MediaGallery />} />
                              <Route path="/notifications" element={<Notifications />} />
                              {/* Super Admin routes */}
                              <Route path="/super-admin/companies" element={<Companies />} />
                              <Route path="/super-admin/users" element={<Users />} />
                              <Route path="/super-admin/packages" element={<Packages />} />
                              <Route path="/super-admin/whatsapp-configs" element={<SuperAdminWhatsAppConfigs />} />
                              <Route path="/super-admin/booking-bot-test" element={<BookingBotTester />} />
                              <Route path="/super-admin/sales-inquiries" element={<SuperAdminSalesInquiries />} />
                              <Route path="/super-admin/analytics" element={<Analytics />} />
                              {/* Admin routes */}
                              <Route path="/admin/agents" element={<Agents />} />
                              <Route path="/admin/whatsapp-configs" element={<AdminWhatsAppConfigs />} />
                              <Route path="/admin/sales-inquiries" element={<AdminSalesInquiries />} />
                              <Route path="/agent/personal-whatsapp" element={<AgentPersonalWhatsApp />} />
                            </Routes>
                          </Suspense>
                        </Layout>
                        </ActiveBulkSendProvider>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Router>
            </NotificationProvider>
            </PersonalWaSessionProvider>
          </SocketProvider>
        </WhatsAppProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
